import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, List } from 'antd';
import ProviderCard from '@components/Clinicians/InvestigationRequest/ProviderCard';
import * as actions from '@actions';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmationModal } from 'vl-common/src/components/Modals';
import { css } from '@emotion/react';

interface Provider {
  addline1: string;
  addline2: string;
  addline3: string;
  addline4: string;
  distance_units: string;
  distance: string;
  group_name: string;
  hospital_id: number;
  hospital_name: string;
  postcode: string;
  region: string;
  service_type_codes: string;
  service_type_code: string;
  ireq_id: number;
}

interface ConfirmInvestigationModalProps {
  visible: boolean;
  onCancel: () => void;
  investigations: Provider[];
  appointmentOutcome: any;
  appointment: {
    appointment_id: number;
  };
  triggerRefetchInvRequests: () => void;
  complete: () => Promise<void>;
  disableCompleteAppointmentButton: boolean;
  alert: any;
  setAlert: React.Dispatch<React.SetStateAction<any>>;
}

const ConfirmInvestigationModal: React.FC<ConfirmInvestigationModalProps> = ({
  visible,
  onCancel,
  investigations,
  appointmentOutcome,
  appointment,
  triggerRefetchInvRequests,
  complete,
  disableCompleteAppointmentButton,
  alert,
  setAlert
}) => {
  const { t } = useTranslation('common');
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [openComplete, setOpenComplete] = useState(false);
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [deleteRequestsInProgress, setDeleteRequestsInProgress] = useState(false);

  return (
    <>
      <ConfirmationModal
        css={css`
          max-height: calc(100vh - 30px);
          overflow-y: auto;
        `}
        open={visible}
        title="Unexpected Outcome for Investigation Request(s)"
        onCancel={onCancel}
        footer={[
          <Button key="cancel" onClick={onCancel} data-testid="close-button">
            {t('Close')}
          </Button>,
          <Button key="primary" type="primary" onClick={() => setOpenComplete(true)} data-testid="complete-appointment">
            {t('Complete appointment')}
          </Button>,
          <Button
            key="remove"
            onClick={() => {
              setIsDeleteModalVisible(true);
            }}
            data-testid="remove-all-investigation-requests"
          >
            {t('Remove requests')}
          </Button>
        ]}
      >
        {t('The appointment outcome is set to ')}
        <b>{appointmentOutcome?.appt_outcome_desc}</b> {t(' with the following active investigation request(s)')}
        <List
          css={css`
            max-height: 400px;
            overflow-y: auto;
            margin: 10px 0;
          `}
          dataSource={investigations}
          renderItem={(item: Provider) => (
            <List.Item>
              <ProviderCard
                hospitalID={item?.hospital_id}
                isCompleted
                provider={item}
                setHospitalID={() => {}}
                confirmInvestigationModalView
              />
            </List.Item>
          )}
        />
        {t('Please review and remove any requests that are no longer required, the button be')}
      </ConfirmationModal>

      <ConfirmationModal
        open={openComplete}
        closable={false}
        onOk={() => complete()}
        okText="Yes"
        onCancel={() => setOpenComplete(false)}
        okButtonProps={{
          disabled: disableCompleteAppointmentButton,
          loading: disableCompleteAppointmentButton
        }}
        cancelButtonProps={{
          disabled: disableCompleteAppointmentButton
        }}
        title="Complete appointment?"
      >
        {t('Completing the appointment will send all relevant document to the Patient’s Port')}
        {alert && (
          <Alert
            className="login-alert"
            message={alert.message}
            type={alert.type}
            afterClose={() => setAlert(null)}
            showIcon
          />
        )}
      </ConfirmationModal>

      <ConfirmationModal
        open={isDeleteModalVisible}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{
          disabled: deleteRequestsInProgress,
          loading: deleteRequestsInProgress
        }}
        cancelButtonProps={{
          disabled: deleteRequestsInProgress
        }}
        onOk={async () => {
          try {
            setDeleteRequestsInProgress(true);
            const promises = investigations.map(async (investigationRequest: Provider) => {
              await dispatch(
                actions.deleteInvestigationRequest(appointment.appointment_id, investigationRequest.ireq_id)
              );
              return queryClient.invalidateQueries({ queryKey: [['getLetterFiles', appointment.appointment_id]] });
            });

            await Promise.all(promises);
            triggerRefetchInvRequests();
            setIsDeleteModalVisible(false);
            setDeleteRequestsInProgress(false);
            onCancel();
          } catch {
            setDeleteRequestsInProgress(false);
          }
        }}
        onCancel={() => setIsDeleteModalVisible(false)}
      >
        {t('Are you sure you want do delete all investigation request?')}
      </ConfirmationModal>
    </>
  );
};

export default ConfirmInvestigationModal;
